import { Controller } from "@hotwired/stimulus"
import { Widget } from "@bump-sh/agent-widget"

// Switches the search dialog to the AI assistant view, a
// @bump-sh/agent-widget conversation with the portal agent.
export default class extends Controller {
  static targets = ["conversation", "searchInput"]
  static classes = ["assistant"]

  static values = {
    agentUrl: String,
    portalUrl: String,
    portalName: String,
    hubUrl: String,
    hubName: String
  }

  disconnect() {
    this.#destroyWidget()
  }

  // Opens the assistant. A search query starts a new conversation with
  // it, otherwise the current conversation goes on.
  async load() {
    this.#showAssistant()
    const query = this.#takeSearchQuery()

    if (query) {
      this.#rebuildWidget()
      await this.widget.sendMessage(query)
    } else {
      this.widget ||= this.#buildWidget()
    }
  }

  reset() {
    // Reset scope to the current documentation root url. Fallback to
    // the hub context.
    this.portalUrlValue = document.body.dataset.docRootUrl || this.hubUrlValue
    if (this.#insideHub()) this.widget.addFrontendTool(this.#extendSearchContextTool())
    this.#showAssistant()
    this.#rebuildWidget()
  }

  backToSearch() {
    this.element.classList.remove(this.assistantClass)
  }

  #showAssistant() {
    this.element.classList.add(this.assistantClass)
  }

  #rebuildWidget() {
    this.#destroyWidget()
    this.widget = this.#buildWidget()
  }

  #destroyWidget() {
    this.widget?.destroy()
    this.widget = undefined
  }

  // The query moves to the conversation: the search is emptied
  #takeSearchQuery() {
    const query = this.searchInputTarget.value.trim()
    if (!query) return

    this.searchInputTarget.value = ""
    this.searchInputTarget.dispatchEvent(new Event("input", {bubbles: true}))
    return query
  }

  #buildWidget() {
    return new Widget({
      mode: "inline",
      title: "",
      placeholder: `Ask anything about the ${this.portalNameValue}`,
      target: this.conversationTarget,
      endpoint: this.agentUrlValue,
      // We recompute the headers as the portal URL may change when we
      // extend the context (to the hub)
      headers: () => {
        return {"Config-Portal-Url": this.portalUrlValue}
      },
      agentContext: {
        portalUrl: this.portalUrlValue,
        "Usage context": this.#usageContext()
      },
      frontendTools: this.#frontendTools()
    })
  }

  #usageContext() {
    const context = `You are inside a documentation portal. Be as concise
      as possible and never paste documentation content: give a short
      answer and, whenever a page is worth reading (operation, schema,
      guide...), call the navigate tool with its URL so the user gets a
      button to open it.`
    if (!this.#insideHub()) return context

    return `${context} When this portal does not cover the question, call
      the extendSearchContext tool instead of giving up.`
  }

  #frontendTools() {
    const tools = [this.#navigateTool()]
    if (this.#insideHub()) tools.push(this.#extendSearchContextTool())
    return tools
  }

  #insideHub() {
    return this.hubUrlValue && this.hubUrlValue !== this.portalUrlValue
  }

  #navigateTool() {
    return {
      name: "navigate",
      description: "Offer the user to open a documentation page (operation, schema, guide...): call it whenever a page is worth reading for the answer",
      parameters: {
        type: "object",
        properties: {
          url: {type: "string"},
          title: {type: "string", description: "Short page name shown on the button, e.g. the operation name (30 characters max)"}
        },
        required: ["url", "title"]
      },
      confirm: "View “{args.title}”",
      execute: this.#navigate.bind(this)
    }
  }

  #extendSearchContextTool() {
    return {
      name: "extendSearchContext",
      description: `When results are empty in this documentation, ask the
            question to the hub containing it, which covers all its
            documentations`,
      parameters: {
        type: "object",
        properties: {message: {type: "string", description: "The user's question"}},
        required: ["message"]
      },
      execute: this.#askTheHub.bind(this)
    }
  }

  // The doc-navigation controller prevents the event when it handles
  // the URL itself (a page of the current documentation): the dialog
  // then closes to reveal the page
  #navigate({url}) {
    const event = this.dispatch("navigate", {detail: {url}, cancelable: true})

    if (event.defaultPrevented) {
      this.element.close()
    } else {
      window.location.assign(url)
    }
  }

  // Switches to the hub by updating the current Portal URL
  #askTheHub({message}) {
    this.portalUrlValue = this.hubUrlValue
    this.widget.element.setAttribute("placeholder", `Ask anything about the ${this.hubNameValue}`)
    this.widget.removeFrontendTool("extendSearchContext")

    // Re-send user message once the agent reply is over
    const removeListenerCallback = this.widget.conversation.on("done", () => {
      setTimeout(() => {
        this.widget.sendMessage(message)
        removeListenerCallback.call(this)
      })
    })

    return `The question is now asked to the ${this.hubNameValue}`
  }
}
